<template>
  <div class="payroll-table">
    <DataTable
      ref="datatableRef"
      :config="datatableConfig"
      :data="periods"
      :refreshButton="refreshButtonConfig"
      :autoLoad="false"
      @row-click="handleRowClick"
      @edit-click="handleEditClick"
      @button-click="handleButtonClick"
      @refresh="loadPeriods"
    />

  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConfirmAction } from '@/composables/useConfirmAction';
import { usePayrollPeriods } from '@/composables/usePayrollPeriods';
import { usePayrollPermissions } from '@/composables/usePayrollPermissions';
import { useFiltro } from '@/composables/useFiltro';
import { eventBus, type EventCallback } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import { permissionsService } from '@/services/permissionsService';

type Id = number | string;

interface CellParams {
  value: unknown;
  data?: Record<string, unknown>;
}

interface PeriodRow {
  id: Id;
  franchise_id: Id;
  user_id: Id;
  status: number | string;
  [key: string]: unknown;
}

interface ButtonClickData {
  action: string;
  data: PeriodRow;
}

const emit = defineEmits<{
  view: [period: PeriodRow];
  calculate: [period: PeriodRow];
  pay: [period: PeriodRow];
  reverse: [period: PeriodRow];
  edit: [period: PeriodRow];
}>();

const { t } = useI18n();
const { confirmAction } = useConfirmAction();
const { listPeriods, getUndoPreview, undoPeriod } = usePayrollPeriods();
const { canCreatePayrollPeriod, canReversePayroll } = usePayrollPermissions();
const { getAvailableFranchises } = useFiltro();

const datatableRef = ref<Record<string, unknown> | null>(null);
const periods = ref<PeriodRow[]>([]);
const franchiseMap = ref<Record<Id, string>>({});
const employeeMap = ref<Record<Id, string>>({});

// DataTable Configuration
const datatableConfig = computed(() => {
  return {
    tableId: 'payrollPeriodsTable',
    pageLength: 25,
    pagination: true,
    columns: tableColumns.value,
    rowTestId: (data: Record<string, unknown>): string => {
      const id = data?.id;
      return id == null ? '' : `payroll-row-${id}`;
    }
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const tableColumns = computed(() => {
  return [
    {
      data: 'id',
      title: t('ID'),
      renderType: 'clickable_id'
    },
    {
      data: 'id',
      title: t('Ações'),
      orderable: false,
      renderType: 'clickable_button',
      buttonText: t('Desfazer'),
      buttonIcon: 'pi pi-undo',
      buttonClass: 'p-button-sm p-button-outlined p-button-danger',
      buttonAction: 'undo',
      // Status 0-4 can be undone (open through confirmed), not paid (5) or archived (-1)
      buttonCondition: (row: PeriodRow): boolean => {
        const status = parseInt(String(row.status));
        return status >= 0 && status <= 4;
      }
    },
    {
      data: 'user_id',
      title: t('Colaborador'),
      cellRenderer: (params: CellParams): string => {
        return String(employeeMap.value[params.value as Id] ?? params.value);
      }
    },
    {
      data: 'franchise_id',
      title: t('Franquia'),
      cellRenderer: (params: CellParams): string => {
        return String(franchiseMap.value[params.value as Id] ?? params.value);
      }
    },
    {
      data: 'period_start_date',
      title: t('Início do Período'),
      renderType: 'date'
    },
    {
      data: 'period_end_date',
      title: t('Fim do Período'),
      renderType: 'date'
    },
    {
      data: 'status',
      title: t('Status'),
      cellRenderer: (params: CellParams): string => {
        // Status constants: 0=open, 1=revenue, 2=expenses, 3=calculated, 4=confirmed, 5=paid, -1=archived
        const statusConfig: Record<string, { label: string; severity: string }> = {
          0: { label: t('Aberto'), severity: 'secondary' },
          1: { label: t('Receitas'), severity: 'info' },
          2: { label: t('Despesas'), severity: 'info' },
          3: { label: t('Calculado'), severity: 'warning' },
          4: { label: t('Confirmado'), severity: 'success' },
          5: { label: t('Pago'), severity: 'success' },
          '-1': { label: t('Arquivado'), severity: 'secondary' }
        };
        const config = statusConfig[String(params.value)] ?? { label: String(params.value), severity: 'secondary' };
        return `<span class="p-tag p-tag-${config.severity}">${config.label}</span>`;
      }
    },
    {
      data: 'revenue_conciliation_amount',
      title: t('Receitas Valor'),
      renderType: 'currency'
    },
    {
      data: 'total_amount',
      title: t('Valor Total'),
      renderType: 'currency',
      cellRenderer: (params: CellParams): string => {
        // Show formatted currency value or zero if not available
        const value = parseFloat(String(params.value)) || 0;
        return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
      }
    },
    {
      data: 'created_at',
      title: t('Created'),
      renderType: 'date'
    }
  ];
});

const loadPeriods: EventCallback = async (): Promise<void> => {
  log('PayrollTable', 'loadPeriods', 'Loading periods');

  try {
    const result = await listPeriods();
    periods.value = result;
    log('PayrollTable', 'loadPeriods', 'Loaded periods', result.length);
  } catch (error) {
    console.error('Error loading payroll periods:', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar períodos de folha'));
  }
};

const handleEditClick = (periodId: Id): void => {
  log('PayrollTable', 'handleEditClick', 'Edit clicked', periodId);

  // Find the full period data
  const period = periods.value.find(p => p.id === periodId);
  if (period) {
    emit('edit', period);
  }
};

const handleRowClick = (rowData: unknown): void => {
  const row = rowData as PeriodRow | undefined;
  log('PayrollTable', 'handleRowClick', 'Row clicked', row?.id ?? null);
};

const handleButtonClick = async (buttonData: ButtonClickData): Promise<void> => {
  log('PayrollTable', 'handleButtonClick', 'Button clicked', buttonData);

  if (buttonData.action === 'undo') {
    await handleUndo(buttonData.data);
  }
};

const handleUndo = async (period: PeriodRow): Promise<void> => {
  log('PayrollTable', 'handleUndo', 'Starting undo process', { periodId: period.id });

  try {
    // Get preview to show user what will be affected
    const preview = await getUndoPreview(period.id, period.franchise_id);

    if (!preview) {
      throw new Error(t('Não foi possível obter os dados de pré-visualização'));
    }

    if (!preview.can_undo) {
      eventBus.emit('globalMessage.error', {
        message: preview.reason || t('Este período não pode ser desfeito'),
        duration: 5000
      });
      return;
    }

    // Build confirmation message
    const affected = preview.affected;
    const lines: string[] = [
      t('Esta ação irá remover permanentemente este período de folha de pagamento.'),
      `${t('Execuções de pagamento')}: ${affected.executions_count ?? 0}`,
      `${t('Deduções de usuário')}: ${affected.deductions_count ?? 0}`,
      `${t('Recebimentos desvinculados')}: ${affected.recebimentos_count ?? 0}`,
      `${t('Despesas desvinculadas')}: ${affected.despesas_count ?? 0}`
    ];
    if ((affected.despesas_to_delete_count ?? 0) > 0) {
      lines.push(`${t('Despesas geradas a serem excluídas')}: ${affected.despesas_to_delete_count}`);
    }
    if ((affected.advances_count ?? 0) > 0) {
      lines.push(`${t('Adiantamentos a serem excluídos')}: ${affected.advances_count}`);
    }
    lines.push(t('Esta ação não pode ser desfeita.'));
    const confirmMessage = lines.join(' | ');

    confirmAction({
      message: confirmMessage,
      header: t('Desfazer período de folha'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: t('Sim, desfazer'),
      rejectLabel: t('Cancelar'),
      onAccept: async () => {
        try {
          await undoPeriod(period.id, period.franchise_id);
          eventBus.emit('payroll-period.undone', period.id);
          eventBus.emit('globalMessage.success', {
            message: t('Período de folha desfeito com sucesso'),
            duration: 3000
          });
        } catch (err) {
          const error = err as Error;
          eventBus.emit('globalMessage.error', {
            message: error.message || t('Falha ao desfazer período de folha'),
            duration: 5000
          });
        }
      }
    });
  } catch (err) {
    const error = err as Error;
    log('PayrollTable', 'handleUndo', 'ERROR getting preview', { error: error.message });
    eventBus.emit('globalMessage.error', {
      message: error.message || t('Falha ao verificar período'),
      duration: 5000
    });
  }
};

// Event listeners
onMounted(async () => {
  log('PayrollTable', 'mounted', 'Component mounted, loading initial data');

  // Build franchise map for name lookup
  const franchises = await getAvailableFranchises();
  franchises.forEach((f: { id: Id; name: string }) => {
    franchiseMap.value[f.id] = f.name;
  });
  log('PayrollTable', 'mounted', 'Franchise map built', Object.keys(franchiseMap.value).length);

  // Build employee map for name lookup (employee-centered architecture)
  // Get all employees across all available franchises
  const allEmployees: Array<{ ID: Id; Name: string }> = [];
  for (const franchise of franchises) {
    // Mapa de NOMES (inclui inativos: histórico da folha) — não é um select.
    const funcionarios = permissionsService.getFuncionariosByFranquiaID(Number(franchise.id));
    for (const id in funcionarios) {
      allEmployees.push({ ID: funcionarios[id].funcionario_id, Name: funcionarios[id].funcionario_nome });
    }
  }
  allEmployees.forEach(e => {
    employeeMap.value[e.ID] = e.Name;
  });
  log('PayrollTable', 'mounted', 'Employee map built', Object.keys(employeeMap.value).length);

  // Load initial data
  await loadPeriods();

  // Listen for events
  eventBus.on('payroll-period.created', loadPeriods);
  eventBus.on('payroll-period.calculated', loadPeriods);
  eventBus.on('payroll-period.confirmed', loadPeriods);
  eventBus.on('payroll-period.paid', loadPeriods);
  eventBus.on('payroll-period.reversed', loadPeriods);
  eventBus.on('payroll-period.undone', loadPeriods);
  eventBus.on('expense-conciliation.completed', loadPeriods);
  eventBus.on('revenue-conciliation.completed', loadPeriods);
  eventBus.on('filter.applied', loadPeriods);
  eventBus.on('employeePayroll.periodCreated', loadPeriods);
});

onUnmounted(() => {
  log('PayrollTable', 'beforeUnmount', 'Component unmounting');

  // Clean up event listeners
  eventBus.off('payroll-period.created', loadPeriods);
  eventBus.off('payroll-period.calculated', loadPeriods);
  eventBus.off('payroll-period.confirmed', loadPeriods);
  eventBus.off('payroll-period.paid', loadPeriods);
  eventBus.off('payroll-period.reversed', loadPeriods);
  eventBus.off('payroll-period.undone', loadPeriods);
  eventBus.off('expense-conciliation.completed', loadPeriods);
  eventBus.off('revenue-conciliation.completed', loadPeriods);
  eventBus.off('filter.applied', loadPeriods);
  eventBus.off('employeePayroll.periodCreated', loadPeriods);
});

// Expose methods
defineExpose({
  loadPeriods
});
</script>

<style scoped>
.payroll-table {
  width: 100%;
}

:deep(.actions-cell) {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
}

:deep(.action-btn) {
  border: none;
  background: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

:deep(.action-btn:hover) {
  background-color: var(--surface-100);
}

:deep(.view-btn) {
  color: var(--primary-color);
}

:deep(.calculate-btn) {
  color: var(--blue-500);
}

:deep(.pay-btn) {
  color: var(--green-500);
}

:deep(.reverse-btn) {
  color: var(--red-500);
}

:deep(.status-badge) {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: capitalize;
}

:deep(.status-secondary) {
  background-color: var(--surface-200);
  color: var(--text-color);
}

:deep(.status-info) {
  background-color: var(--blue-100);
  color: var(--blue-700);
}

:deep(.status-success) {
  background-color: var(--green-100);
  color: var(--green-700);
}

:deep(.status-danger) {
  background-color: var(--red-100);
  color: var(--red-700);
}

/* Undo confirmation dialog styles */
.undo-confirm-message {
  line-height: 1.6;
}

.undo-confirm-message p {
  margin: 0 0 0.75rem 0;
}

.undo-confirm-message :deep(.undo-affected-list) {
  margin: 0.5rem 0 1rem 0;
  padding-left: 1.5rem;
  list-style-type: disc;
}

.undo-confirm-message :deep(.undo-affected-list li) {
  margin-bottom: 0.25rem;
}

.undo-confirm-message :deep(.undo-warning) {
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: var(--red-50);
  border-left: 4px solid var(--red-500);
  border-radius: 4px;
  color: var(--red-700);
  font-weight: 500;
}

.undo-confirm-message :deep(.undo-warning i) {
  margin-right: 0.5rem;
}
</style>
